import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  Button,
  Input,
  Select,
  Card,
  DataTable,
  Modal,
} from "../../components/ui";
import { ConfirmModal } from "../../components/ui/Modal";

// Ruoli utente (allineati al backend)
const ROLES = {
  ADMIN: 100000000,
  RSPP: 100000001,
  DIREZIONE: 100000002,
  DIRETTORE_RSA: 100000003,
  AMMINISTRATIVO: 100000004,
  FORNITORE: 100000005,
};

// Tipologie documento che i fornitori possono caricare (value del rapportino)
const TIPI_DOCUMENTO_FORNITORE = ["100000003"];

function DocumentiPage() {
  const { apiRequest, user } = useAuth();
  const { showToast } = useToast();

  // Controlli ruolo utente
  const userRuolo = user?.ruolo;
  const isAdmin = userRuolo === ROLES.ADMIN;
  const isRspp = userRuolo === ROLES.RSPP;
  const isDirezione = userRuolo === ROLES.DIREZIONE;
  const isFornitore = userRuolo === ROLES.FORNITORE;
  const isDirettoreRsa = userRuolo === ROLES.DIRETTORE_RSA;
  const isAmministrativo = userRuolo === ROLES.AMMINISTRATIVO;

  // Permessi basati su ruolo
  const canUpload =
    isAdmin ||
    isRspp ||
    isDirezione ||
    isDirettoreRsa ||
    isAmministrativo ||
    isFornitore;
  const canDelete = isAdmin; // Solo Admin può eliminare
  const canEditVisibility = isAdmin || isRspp; // Solo Admin/RSPP possono cambiare visibilità
  const canSeeAllDocuments = isAdmin || isRspp || isDirezione;

  // Data state
  const [documenti, setDocumenti] = useState([]);
  const [rsaList, setRsaList] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [contratti, setContratti] = useState([]);
  const [tipiDocumento, setTipiDocumento] = useState([]);
  const [immobili, setImmobili] = useState([]); // Immobili filtrati per RSA selezionata
  const [loading, setLoading] = useState(true);
  const [loadingImmobili, setLoadingImmobili] = useState(false);

  // Tipi documento disponibili per upload (fornitori solo rapportini)
  const tipiDocumentoDisponibili = isFornitore
    ? tipiDocumento.filter((t) => TIPI_DOCUMENTO_FORNITORE.includes(t.value))
    : tipiDocumento;

  // Helper per ottenere label tipo documento
  const getTipoDocumentoLabel = (value) => {
    const found = tipiDocumento.find((t) => t.value === String(value));
    return found ? found.label : "-";
  };

  // Filters
  const [filters, setFilters] = useState({
    rsaId: "",
    fornitoreId: "",
    tipodocumento: "",
    anno: "",
  });

  // Modal states
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [editForm, setEditForm] = useState({
    phc_name: "",
    phc_tipodocumento: "",
    phc_descrizione: "",
    phc_datadocumento: "",
    phc_datascadenza: "",
    phc_annoriferimento: "",
    phc_visibilefornitore: false,
  });

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    files: [],
    phc_name: "",
    phc_tipodocumento: "",
    phc_descrizione: "",
    phc_rsa_id: "",
    phc_immobile_id: "",
    phc_fornitore_id: "",
    phc_contratto_id: "",
    phc_datadocumento: "",
    phc_datascadenza: "",
    phc_annoriferimento: new Date().getFullYear().toString(),
    phc_visibilefornitore: false,
  });

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  // Carica immobili quando cambia RSA selezionata nel form upload
  useEffect(() => {
    const loadImmobili = async () => {
      if (!uploadForm.phc_rsa_id) {
        setImmobili([]);
        return;
      }

      setLoadingImmobili(true);
      try {
        const result = await apiRequest(
          `/rsa/${uploadForm.phc_rsa_id}/immobili`,
        );
        setImmobili(result.value || []);
      } catch (error) {
        console.error("Error loading immobili:", error);
        setImmobili([]);
      } finally {
        setLoadingImmobili(false);
      }
    };

    loadImmobili();
  }, [uploadForm.phc_rsa_id, apiRequest]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [
        documentiData,
        rsaData,
        fornitoriData,
        contrattiData,
        tipiDocData,
      ] = await Promise.all([
        apiRequest("/documenti"),
        apiRequest("/rsa"),
        apiRequest("/fornitori"),
        apiRequest("/contratti"),
        apiRequest("/admin/dataverse/tipodocumento-options"),
      ]);

      setDocumenti(documentiData.value || []);
      setRsaList(rsaData.value || []);
      setFornitori(fornitoriData.value || []);
      setContratti(contrattiData.value || []);

      // Converti tipi documento in formato per Select (value come stringa)
      const tipiFormatted = (tipiDocData.options || []).map((t) => ({
        value: String(t.value),
        label: t.label,
      }));
      setTipiDocumento(tipiFormatted);
    } catch (error) {
      console.error("Error loading data:", error);
      showToast("Errore nel caricamento dei dati", "error");
    } finally {
      setLoading(false);
    }
  };

  // Filter documents
  const filteredDocumenti = documenti.filter((doc) => {
    if (filters.rsaId && (doc.rsaId || doc.phc_rsa_id) !== filters.rsaId)
      return false;
    if (
      filters.fornitoreId &&
      (doc.fornitoreId || doc.phc_fornitore_id) !== filters.fornitoreId
    )
      return false;
    if (
      filters.tipodocumento &&
      String(doc.tipoDocumento || doc.phc_tipodocumento) !==
        filters.tipodocumento
    )
      return false;
    if (
      filters.anno &&
      String(doc.annoRiferimento || doc.phc_annoriferimento) !== filters.anno
    )
      return false;
    return true;
  });

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Handle upload form change
  const handleUploadChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file") {
      const newFiles = Array.from(files || []);
      if (!newFiles.length) return;
      setUploadForm((prev) => {
        // Aggiunge i nuovi file evitando duplicati (stesso nome + dimensione)
        const existingKeys = new Set(
          prev.files.map((f) => `${f.name}_${f.size}`),
        );
        const merged = [
          ...prev.files,
          ...newFiles.filter((f) => !existingKeys.has(`${f.name}_${f.size}`)),
        ];
        return {
          ...prev,
          files: merged,
          // Auto-fill nome dal primo file, solo se non ancora impostato
          phc_name: prev.phc_name || merged[0].name.replace(/\.[^/.]+$/, ""),
        };
      });
      e.target.value = ""; // Permette di riselezionare lo stesso file
    } else if (name === "phc_rsa_id") {
      // Reset immobile quando cambia RSA
      setUploadForm((prev) => ({
        ...prev,
        phc_rsa_id: value,
        phc_immobile_id: "",
      }));
    } else {
      setUploadForm((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  // Remove a file from the pending upload selection
  const removeUploadFile = (index) => {
    setUploadForm((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  // Handle upload
  const handleUpload = async (e) => {
    e.preventDefault();

    if (!uploadForm.files.length) {
      showToast("Seleziona almeno un file da caricare", "error");
      return;
    }

    if (!uploadForm.phc_name) {
      showToast("Inserisci un nome per il documento", "error");
      return;
    }

    if (!uploadForm.phc_rsa_id) {
      showToast("Seleziona una RSA (campo obbligatorio)", "error");
      return;
    }

    if (!uploadForm.phc_tipodocumento) {
      showToast("Seleziona un tipo documento", "error");
      return;
    }

    setUploading(true);
    try {
      // Create FormData for multipart upload - campi separati come richiesto dal backend
      const formData = new FormData();
      uploadForm.files.forEach((f) => formData.append("files", f));
      formData.append("nome", uploadForm.phc_name);
      formData.append("tipoDocumento", uploadForm.phc_tipodocumento);
      formData.append("rsaId", uploadForm.phc_rsa_id);

      if (uploadForm.phc_immobile_id) {
        formData.append("immobileId", uploadForm.phc_immobile_id);
      }
      if (uploadForm.phc_fornitore_id) {
        formData.append("fornitoreId", uploadForm.phc_fornitore_id);
      }
      if (uploadForm.phc_contratto_id) {
        formData.append("contrattoId", uploadForm.phc_contratto_id);
      }
      if (uploadForm.phc_datadocumento) {
        formData.append("dataDocumento", uploadForm.phc_datadocumento);
      }
      if (uploadForm.phc_datascadenza) {
        formData.append("dataScadenza", uploadForm.phc_datascadenza);
      }
      if (uploadForm.phc_descrizione) {
        formData.append("descrizione", uploadForm.phc_descrizione);
      }
      formData.append("visibileFornitore", uploadForm.phc_visibilefornitore);

      await apiRequest("/documenti/upload", {
        method: "POST",
        body: formData,
        headers: {}, // Let browser set content-type for FormData
      });

      showToast("Documento caricato con successo", "success");
      setUploadModalOpen(false);
      resetUploadForm();
      loadData();
    } catch (error) {
      console.error("Error uploading document:", error);
      showToast(error.message || "Errore durante il caricamento", "error");
    } finally {
      setUploading(false);
    }
  };

  // Reset upload form
  const resetUploadForm = () => {
    setUploadForm({
      files: [],
      phc_name: "",
      phc_tipodocumento: "",
      phc_descrizione: "",
      phc_rsa_id: "",
      phc_immobile_id: "",
      phc_fornitore_id: "",
      phc_contratto_id: "",
      phc_datadocumento: "",
      phc_datascadenza: "",
      phc_annoriferimento: new Date().getFullYear().toString(),
      phc_visibilefornitore: false,
    });
    setImmobili([]); // Reset lista immobili
  };

  // View document detail
  const viewDocument = async (doc) => {
    setSelectedDoc(doc);
    setDetailModalOpen(true);
    // Carica il dettaglio completo (include l'elenco di tutti i file allegati)
    try {
      const detail = await apiRequest(`/documenti/${doc.id}`);
      setSelectedDoc((prev) =>
        prev?.id === doc.id ? { ...doc, ...detail } : prev,
      );
    } catch (error) {
      console.error("Error loading document detail:", error);
    }
  };

  // Download document tramite API (gestisce permessi)
  const downloadDocument = async (doc) => {
    const docId = doc.id || doc.phc_documentoid;
    if (!docId) {
      showToast("ID documento non disponibile", "warning");
      return;
    }

    try {
      // Usa l'endpoint API che gestisce i permessi e restituisce il file
      const response = await apiRequest(`/documenti/${docId}/download`, {
        method: "GET",
        responseType: "blob",
      });

      // Se l'API ritorna un blob, crea un link per il download
      if (response instanceof Blob) {
        const url = window.URL.createObjectURL(response);
        const link = document.createElement("a");
        link.href = url;
        link.download = doc.nome || doc.phc_name || "documento";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Fallback: apri URL SharePoint diretto (per compatibilita')
        const url = doc.urlSharepoint || doc.phc_urlsharepoint;
        if (url) {
          window.open(url, "_blank");
        } else {
          showToast("Download non disponibile", "warning");
        }
      }
    } catch (error) {
      console.error("Download error:", error);
      // Fallback: prova ad aprire URL SharePoint diretto
      const url = doc.urlSharepoint || doc.phc_urlsharepoint;
      if (url) {
        window.open(url, "_blank");
      } else {
        showToast("Errore durante il download", "error");
      }
    }
  };

  // Delete document
  const handleDelete = async () => {
    if (!selectedDoc) return;

    try {
      await apiRequest(`/documenti/${selectedDoc.id}`, {
        method: "DELETE",
      });
      showToast("Documento eliminato con successo", "success");
      setDeleteConfirmOpen(false);
      setDetailModalOpen(false);
      setSelectedDoc(null);
      loadData();
    } catch (error) {
      console.error("Error deleting document:", error);
      showToast(error.message || "Errore durante l'eliminazione", "error");
    }
  };

  // Open edit modal
  const openEditModal = (doc) => {
    setEditForm({
      phc_name: doc.nome || doc.phc_name || "",
      phc_tipodocumento: String(
        doc.tipoDocumento || doc.phc_tipodocumento || "",
      ),
      phc_descrizione: doc.descrizione || doc.phc_descrizione || "",
      phc_datadocumento: doc.dataDocumento || doc.phc_datadocumento || "",
      phc_datascadenza: doc.dataScadenza || doc.phc_datascadenza || "",
      phc_annoriferimento: String(
        doc.annoRiferimento || doc.phc_annoriferimento || "",
      ),
      phc_visibilefornitore:
        doc.visibileFornitore || doc.phc_visibilefornitore || false,
    });
    setSelectedDoc(doc);
    setDetailModalOpen(false);
    setEditModalOpen(true);
  };

  // Handle edit form change
  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Save document changes
  const handleSaveEdit = async (e) => {
    e.preventDefault();

    if (!selectedDoc) return;

    setSaving(true);
    try {
      const updateData = {
        nome: editForm.phc_name,
        tipoDocumento: parseInt(editForm.phc_tipodocumento),
        descrizione: editForm.phc_descrizione || null,
        dataDocumento: editForm.phc_datadocumento || null,
        dataScadenza: editForm.phc_datascadenza || null,
        annoRiferimento: editForm.phc_annoriferimento
          ? parseInt(editForm.phc_annoriferimento)
          : null,
        visibileFornitore: editForm.phc_visibilefornitore,
      };

      await apiRequest(`/documenti/${selectedDoc.id}`, {
        method: "PUT",
        body: JSON.stringify(updateData),
      });

      showToast("Documento aggiornato con successo", "success");
      setEditModalOpen(false);
      setSelectedDoc(null);
      loadData();
    } catch (error) {
      console.error("Error updating document:", error);
      showToast(error.message || "Errore durante l'aggiornamento", "error");
    } finally {
      setSaving(false);
    }
  };

  // Prepare options for selects
  const rsaOptions = rsaList.map((r) => ({
    value: r.id,
    label: r.nome || r.phc_name,
  }));
  const fornitoriOptions = fornitori.map((f) => ({
    value: f.id,
    label: f.nome || f.phc_name,
  }));
  const contrattiOptions = contratti.map((c) => ({
    value: c.id,
    label: c.nome || c.phc_name,
  }));
  const immobiliOptions = immobili.map((i) => ({
    value: i.id,
    label: i.nome || i.phc_name,
  }));

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
  }));

  // Table columns
  const columns = [
    {
      key: "nome",
      label: "Documento",
      sortable: true,
      render: (row) => {
        if (!row) return null;
        return (
          <div className="flex items-center gap-3">
            <div className="text-2xl">
              {getFileIcon(row.nome || row.phc_name)}
            </div>
            <div>
              <div className="font-medium text-gray-900 flex items-center gap-2">
                {row.nome || row.phc_name}
                {row.allegatiCount > 1 && (
                  <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                    {row.allegatiCount} file
                  </span>
                )}
              </div>
              {(row.descrizione || row.phc_descrizione) && (
                <div className="text-xs text-gray-500 truncate max-w-xs">
                  {row.descrizione || row.phc_descrizione}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "tipo",
      label: "Tipo",
      render: (row) => {
        if (!row) return null;
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
            {getTipoDocumentoLabel(row.tipoDocumento || row.phc_tipodocumento)}
          </span>
        );
      },
    },
    {
      key: "rsa",
      label: "RSA",
      render: (row) => row?.rsa || "-",
    },
    {
      key: "fornitore",
      label: "Fornitore",
      render: (row) => row?.fornitore || "-",
    },
    {
      key: "data",
      label: "Data",
      render: (row) => {
        if (!row) return "-";
        const data = row.dataDocumento || row.phc_datadocumento;
        return data ? new Date(data).toLocaleDateString("it-IT") : "-";
      },
    },
    {
      key: "scadenza",
      label: "Scadenza",
      render: (row) => {
        if (!row) return "-";
        const scadenza = row.dataScadenza || row.phc_datascadenza;
        if (!scadenza) return "-";
        const date = new Date(scadenza);
        const isExpired = date < new Date();
        return (
          <span className={isExpired ? "text-red-600 font-medium" : ""}>
            {date.toLocaleDateString("it-IT")}
          </span>
        );
      },
    },
    {
      key: "visibile",
      label: "Visibile",
      render: (row) => {
        if (!row) return "-";
        const visibile = row.visibileFornitore || row.phc_visibilefornitore;
        return visibile ? "👁️ Sì" : "🔒 No";
      },
    },
    {
      key: "actions",
      label: "Azioni",
      render: (row) => {
        if (!row) return null;
        return (
          <div className="flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadDocument(row);
              }}
              className="p-1 text-primary-600 hover:text-primary-800"
              title="Scarica"
            >
              📥
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                viewDocument(row);
              }}
              className="p-1 text-gray-600 hover:text-gray-800"
              title="Dettagli"
            >
              👁️
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <a href="/admin" className="hover:text-primary-600">
              Admin
            </a>
            <span>/</span>
            <span>Documenti</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Archivio Documenti
          </h1>
          <p className="text-gray-500 mt-1">
            Gestione documenti caricati su SharePoint
          </p>
        </div>

        {canUpload && (
          <Button variant="primary" onClick={() => setUploadModalOpen(true)}>
            📤 Carica Documento
          </Button>
        )}
      </div>

      {/* Info banner per ruoli specifici */}
      {(isDirettoreRsa || isAmministrativo) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">📋</span>
            <div>
              <h3 className="font-semibold text-blue-800">
                Documenti della tua RSA
              </h3>
              <p className="text-sm text-blue-700 mt-1">
                Visualizzi tutti i documenti associati alla tua struttura:
                contratti, certificazioni, planimetrie, dichiarazioni di
                conformità, documentazione impianti e altro.
                {canUpload && " Puoi caricare nuovi documenti per la tua RSA."}
              </p>
            </div>
          </div>
        </div>
      )}

      {isFornitore && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <span className="text-2xl">📝</span>
            <div>
              <h3 className="font-semibold text-amber-800">
                Documenti visibili
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Visualizzi solo i documenti relativi ai tuoi contratti che sono
                stati resi visibili dall'amministratore. Puoi caricare
                rapportini di manutenzione.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-2xl font-bold text-gray-900">
            {documenti.length}
          </div>
          <div className="text-sm text-gray-500">Totale</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-green-600">
            {
              documenti.filter(
                (d) => (d.tipoDocumento || d.phc_tipodocumento) === 100000000,
              ).length
            }
          </div>
          <div className="text-sm text-gray-500">Contratti</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-primary-600">
            {
              documenti.filter(
                (d) => (d.tipoDocumento || d.phc_tipodocumento) === 100000005,
              ).length
            }
          </div>
          <div className="text-sm text-gray-500">Certificazioni</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-blue-600">
            {
              documenti.filter((d) => {
                const tipo = d.tipoDocumento || d.phc_tipodocumento;
                // Documenti immobili: planimetrie, schede tecniche, conformità, impianti
                return [100000012, 100000013, 100000014, 100000015].includes(
                  tipo,
                );
              }).length
            }
          </div>
          <div className="text-sm text-gray-500">Doc. Immobili</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-purple-600">
            {
              documenti.filter(
                (d) => (d.tipoDocumento || d.phc_tipodocumento) === 100000003,
              ).length
            }
          </div>
          <div className="text-sm text-gray-500">Rapportini</div>
        </Card>
        <Card className="p-4">
          <div className="text-2xl font-bold text-red-600">
            {
              documenti.filter((d) => {
                const scadenza = d.dataScadenza || d.phc_datascadenza;
                return scadenza && new Date(scadenza) < new Date();
              }).length
            }
          </div>
          <div className="text-sm text-gray-500">Scaduti</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Select
            name="rsaId"
            value={filters.rsaId}
            onChange={handleFilterChange}
            options={rsaOptions}
            placeholder="Filtra per RSA..."
          />
          <Select
            name="fornitoreId"
            value={filters.fornitoreId}
            onChange={handleFilterChange}
            options={fornitoriOptions}
            placeholder="Filtra per Fornitore..."
          />
          <Select
            name="tipodocumento"
            value={filters.tipodocumento}
            onChange={handleFilterChange}
            options={tipiDocumento}
            placeholder="Filtra per Tipo..."
          />
          <Select
            name="anno"
            value={filters.anno}
            onChange={handleFilterChange}
            options={yearOptions}
            placeholder="Filtra per Anno..."
          />
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <DataTable
          data={filteredDocumenti}
          columns={columns}
          loading={loading}
          searchable
          searchPlaceholder="Cerca documento..."
          emptyMessage="Nessun documento trovato"
          onRowClick={viewDocument}
        />
      </Card>

      {/* Upload Modal */}
      <Modal
        isOpen={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          resetUploadForm();
        }}
        title="Carica Documento"
        size="lg"
      >
        <form onSubmit={handleUpload} className="space-y-4">
          {/* File Upload Area */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              id="file-upload"
              className="hidden"
              multiple
              onChange={handleUploadChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="text-4xl mb-2">📎</div>
              <div className="text-gray-700">
                Clicca o trascina uno o più file qui
              </div>
              <div className="text-sm text-gray-500 mt-1">
                PDF, Word, Excel, Immagini (max 200MB per caricamento). Un
                documento può essere composto da più file (es. una DICO su più
                PDF).
              </div>
              {uploadForm.files.length > 0 && (
                <div className="text-primary-600 text-sm mt-2">
                  Clicca per aggiungere altri file
                </div>
              )}
            </label>
          </div>

          {uploadForm.files.length > 0 && (
            <div className="space-y-2">
              {uploadForm.files.map((f, index) => (
                <div
                  key={`${f.name}_${f.size}_${index}`}
                  className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2"
                >
                  <div className="text-xl">{getFileIcon(f.name)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {f.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {(f.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeUploadFile(index)}
                    className="p-1 text-gray-400 hover:text-red-600"
                    title="Rimuovi file"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <Input
            label="Nome Documento *"
            name="phc_name"
            value={uploadForm.phc_name}
            onChange={handleUploadChange}
            placeholder="es. Certificato Impianto Elettrico 2026"
            maxLength={200}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Tipo Documento *"
              name="phc_tipodocumento"
              value={uploadForm.phc_tipodocumento}
              onChange={handleUploadChange}
              options={tipiDocumentoDisponibili}
              placeholder="Seleziona tipo..."
              required
            />

            <Input
              label="Anno Riferimento"
              name="phc_annoriferimento"
              type="number"
              value={uploadForm.phc_annoriferimento}
              onChange={handleUploadChange}
              min="1900"
              max="2100"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="RSA *"
              name="phc_rsa_id"
              value={uploadForm.phc_rsa_id}
              onChange={handleUploadChange}
              options={rsaOptions}
              placeholder="Seleziona RSA..."
              required
            />

            <Select
              label="Immobile"
              name="phc_immobile_id"
              value={uploadForm.phc_immobile_id}
              onChange={handleUploadChange}
              options={immobiliOptions}
              placeholder={
                loadingImmobili
                  ? "Caricamento..."
                  : uploadForm.phc_rsa_id
                    ? "Seleziona immobile..."
                    : "Seleziona prima una RSA"
              }
              disabled={!uploadForm.phc_rsa_id || loadingImmobili}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Fornitore"
              name="phc_fornitore_id"
              value={uploadForm.phc_fornitore_id}
              onChange={handleUploadChange}
              options={fornitoriOptions}
              placeholder="Associa a Fornitore..."
            />

            <Select
              label="Contratto"
              name="phc_contratto_id"
              value={uploadForm.phc_contratto_id}
              onChange={handleUploadChange}
              options={contrattiOptions}
              placeholder="Associa a Contratto..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Data Documento"
              name="phc_datadocumento"
              type="date"
              value={uploadForm.phc_datadocumento}
              onChange={handleUploadChange}
            />

            <Input
              label="Data Scadenza"
              name="phc_datascadenza"
              type="date"
              value={uploadForm.phc_datascadenza}
              onChange={handleUploadChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrizione
            </label>
            <textarea
              name="phc_descrizione"
              value={uploadForm.phc_descrizione}
              onChange={handleUploadChange}
              placeholder="Descrizione del documento..."
              className="form-input w-full h-20"
              maxLength={2000}
            />
          </div>

          {/* Checkbox visibilità fornitore - solo Admin/RSPP possono modificarlo */}
          {canEditVisibility && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="phc_visibilefornitore"
                checked={uploadForm.phc_visibilefornitore}
                onChange={handleUploadChange}
                className="h-4 w-4 rounded border-gray-300 text-primary-600"
              />
              <span className="text-gray-700">Visibile al fornitore</span>
            </label>
          )}

          {/* Info ruolo fornitore */}
          {isFornitore && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              💡 Come fornitore puoi caricare solo rapportini di manutenzione. I
              documenti saranno automaticamente visibili agli amministratori.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setUploadModalOpen(false);
                resetUploadForm();
              }}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={
                uploading ||
                !uploadForm.files.length ||
                !uploadForm.phc_name ||
                !uploadForm.phc_rsa_id ||
                !uploadForm.phc_tipodocumento
              }
            >
              {uploading ? "Caricamento..." : "Carica"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedDoc(null);
        }}
        title="Dettagli Documento"
        size="lg"
      >
        {selectedDoc && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="text-5xl">
                {getFileIcon(selectedDoc.nome || selectedDoc.phc_name)}
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {selectedDoc.nome || selectedDoc.phc_name}
                </h3>
                <p className="text-gray-500">
                  {getTipoDocumentoLabel(
                    selectedDoc.tipoDocumento || selectedDoc.phc_tipodocumento,
                  )}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
              <div>
                <div className="text-xs text-gray-500">RSA</div>
                <div className="font-medium">{selectedDoc.rsa || "-"}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Fornitore</div>
                <div className="font-medium">
                  {selectedDoc.fornitore || "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Data Documento</div>
                <div className="font-medium">
                  {selectedDoc.dataDocumento || selectedDoc.phc_datadocumento
                    ? new Date(
                        selectedDoc.dataDocumento ||
                          selectedDoc.phc_datadocumento,
                      ).toLocaleDateString("it-IT")
                    : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Data Scadenza</div>
                <div
                  className={`font-medium ${
                    (selectedDoc.dataScadenza ||
                      selectedDoc.phc_datascadenza) &&
                    new Date(
                      selectedDoc.dataScadenza || selectedDoc.phc_datascadenza,
                    ) < new Date()
                      ? "text-red-600"
                      : ""
                  }`}
                >
                  {selectedDoc.dataScadenza || selectedDoc.phc_datascadenza
                    ? new Date(
                        selectedDoc.dataScadenza ||
                          selectedDoc.phc_datascadenza,
                      ).toLocaleDateString("it-IT")
                    : "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Anno Riferimento</div>
                <div className="font-medium">
                  {selectedDoc.annoRiferimento ||
                    selectedDoc.phc_annoriferimento ||
                    "-"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">
                  Visibile al Fornitore
                </div>
                <div className="font-medium">
                  {selectedDoc.visibileFornitore ||
                  selectedDoc.phc_visibilefornitore
                    ? "Sì"
                    : "No"}
                </div>
              </div>
            </div>

            {(selectedDoc.descrizione || selectedDoc.phc_descrizione) && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Descrizione</div>
                <div className="text-gray-700">
                  {selectedDoc.descrizione || selectedDoc.phc_descrizione}
                </div>
              </div>
            )}

            {selectedDoc.allegati?.length > 1 && (
              <div>
                <div className="text-xs text-gray-500 mb-2">
                  File allegati ({selectedDoc.allegati.length})
                </div>
                <div className="space-y-2">
                  {selectedDoc.allegati.map((a) => (
                    <div
                      key={a.id}
                      className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2"
                    >
                      <div className="text-xl">{getFileIcon(a.name)}</div>
                      <div className="flex-1 min-w-0 font-medium text-gray-900 truncate">
                        {a.name}
                      </div>
                      <button
                        type="button"
                        onClick={() => window.open(a.webUrl, "_blank")}
                        className="p-1 text-primary-600 hover:text-primary-800"
                        title="Scarica"
                      >
                        📥
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t">
              {canDelete ? (
                <Button
                  variant="danger"
                  onClick={() => setDeleteConfirmOpen(true)}
                >
                  🗑️ Elimina
                </Button>
              ) : (
                <div /> // Placeholder per mantenere layout
              )}
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDetailModalOpen(false);
                    setSelectedDoc(null);
                  }}
                >
                  Chiudi
                </Button>
                {canEditVisibility && (
                  <Button
                    variant="secondary"
                    onClick={() => openEditModal(selectedDoc)}
                  >
                    ✏️ Modifica
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={() => downloadDocument(selectedDoc)}
                >
                  📥 Scarica
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedDoc(null);
        }}
        title="Modifica Documento"
        size="lg"
      >
        <form onSubmit={handleSaveEdit} className="space-y-4">
          <Input
            label="Nome Documento *"
            name="phc_name"
            value={editForm.phc_name}
            onChange={handleEditChange}
            placeholder="Nome del documento"
            maxLength={200}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Tipo Documento *"
              name="phc_tipodocumento"
              value={editForm.phc_tipodocumento}
              onChange={handleEditChange}
              options={tipiDocumento}
              placeholder="Seleziona tipo..."
              required
            />

            <Input
              label="Anno Riferimento"
              name="phc_annoriferimento"
              type="number"
              value={editForm.phc_annoriferimento}
              onChange={handleEditChange}
              min="1900"
              max="2100"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Data Documento"
              name="phc_datadocumento"
              type="date"
              value={
                editForm.phc_datadocumento
                  ? editForm.phc_datadocumento.split("T")[0]
                  : ""
              }
              onChange={handleEditChange}
            />

            <Input
              label="Data Scadenza"
              name="phc_datascadenza"
              type="date"
              value={
                editForm.phc_datascadenza
                  ? editForm.phc_datascadenza.split("T")[0]
                  : ""
              }
              onChange={handleEditChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrizione
            </label>
            <textarea
              name="phc_descrizione"
              value={editForm.phc_descrizione}
              onChange={handleEditChange}
              placeholder="Descrizione del documento..."
              className="form-input w-full h-20"
              maxLength={2000}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="phc_visibilefornitore"
                checked={editForm.phc_visibilefornitore}
                onChange={handleEditChange}
                className="h-5 w-5 rounded border-gray-300 text-primary-600"
              />
              <div>
                <span className="font-medium text-gray-900">
                  Visibile al fornitore
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  Se attivato, il fornitore associato al documento potrà
                  visualizzarlo e scaricarlo.
                </p>
              </div>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEditModalOpen(false);
                setSelectedDoc(null);
              }}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || !editForm.phc_name}
            >
              {saving ? "Salvataggio..." : "Salva Modifiche"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Elimina Documento"
        message={`Sei sicuro di voler eliminare il documento "${selectedDoc?.nome || selectedDoc?.phc_name}"? Il file verrà rimosso anche da SharePoint.`}
        confirmLabel="Elimina"
        variant="danger"
      />
    </div>
  );
}

// Helper function to get file icon based on filename
function getFileIcon(filename) {
  if (!filename) return "📄";
  const ext = filename.split(".").pop()?.toLowerCase();
  const icons = {
    pdf: "📕",
    doc: "📘",
    docx: "📘",
    xls: "📗",
    xlsx: "📗",
    png: "🖼️",
    jpg: "🖼️",
    jpeg: "🖼️",
    gif: "🖼️",
  };
  return icons[ext] || "📄";
}

export default DocumentiPage;
